import { useEffect, useRef, useState, type ReactNode } from "react"
import type { AbilityLevel, AccusationTrial, GameState } from "./types"
import {
  addMoney,
  buyArmor,
  advanceDay,
  applyBattleCasualties,
  completeAbilityTraining,
  ARMY_SCALE,
  createNewGame,
  createNewLoop,
  destroyGarrisonAt,
  grantCommandPoints,
  grantKnowledge,
  grantUnitsAtLocation,
  clearAccusation,
  clearWorldFlag,
  killPlayer,
  leaveUnitsAtLocation,
  raiseFactionTrust,
  releaseUnitsFromArmy,
  setWorldFlag,
  startNewLoop,
  subjectFaction,
  takeAllUnitsAtLocation,
  takeUnitsAtLocation,
  trainAbility,
  travel,
} from "./game/loopEngine"
import {
  accusationFacedFlag,
  getAccusationBattleId,
  getClearedKnowledge,
  getPendingTrial,
} from "./game/accusationEngine"
import {
  discoverIdentity,
  discoverMotivation,
  FINAL_BATTLE_ID,
  getStrategyForBattle,
  isAntiPlayerBattle,
  recordEncounter,
  recordMethodFailed,
  resolveAntiPlayerMove,
  severAntiPlayer,
} from "./game/antiPlayerEngine"
import {
  consumeSpiritAudience,
  getEnemyFactionDiscoveries,
  getPendingSpiritScene,
  getSceneIdForDialogue,
  markSpiritQuestionAsked,
  markSpiritSceneSeen,
  recordSpiritDiscoveries,
} from "./game/spiritEngine"
import { isAtLeastLevel, getAbilityLevel } from "./game/abilityEngine"
import { spiritQuestions } from "./game/content"
import {
  applyHealingRecovery,
  getPlayerBattleModifiers,
  type BattleResolution,
} from "./game/battleEngine"
import { resolveTriggeredEvents } from "./game/eventEffects"
import { discoverReachableLocations } from "./game/mapEngine"
import {
  applyFactionActionEffects,
  getFactionActionById,
  getFactionActionCostDays,
} from "./game/questEngine"
import { commandPointsUsed, getStacksAtLocation } from "./game/armyEngine"
import { hasSavedGame, loadGame, saveGame } from "./game/saveEngine"
import {
  factions,
  getAbilityById,
  getBattleById,
  getDialogueById,
  getJournalEntryByKnowledgeId,
  getLocationById,
  getRouteById,
} from "./game/content"
import { getTrust, TRUST_ORDER } from "./game/factionEngine"
import { getTravelBlockReason } from "./game/travelEngine"
import { getLoopProgress, getTimeOfDay } from "./game/atmosphereEngine"
import { getBattleBackdropValue } from "./game/sprites"
import { getBattlePaceDelayMs, useSettings } from "./game/settings"
import {
  trackAbilityProgression,
  trackBattleResolved,
  trackDay30Survival,
  trackDeath,
  trackFactionCompletion,
  trackGameCompleted,
  trackLongestSurvival,
  trackLoopStarted,
} from "./game/analytics"
import { recordHistory, recordHistoryMany } from "./game/historyLog"
import { AppShell } from "./components/AppShell"
import { NAV_ITEMS, type NavTarget } from "./components/nav"
import { DayDigest } from "./components/DayDigest"
import { LoopTransition } from "./components/LoopTransition"
import { ConfirmDialog } from "./components/ConfirmDialog"
import { Toasts } from "./components/Toasts"
import { useToasts } from "./components/useToasts"
import { ScheduleScreen } from "./screens/ScheduleScreen"
import { MainMenuScreen } from "./screens/MainMenuScreen"
import { AvatarSelectScreen } from "./screens/AvatarSelectScreen"
import { WorldMapScreen } from "./screens/WorldMapScreen"
import { LocationScreen } from "./screens/LocationScreen"
import { TrialScreen } from "./screens/TrialScreen"
import { ArmyScreen } from "./screens/ArmyScreen"
import { FactionsScreen } from "./screens/FactionsScreen"
import { EventScreen } from "./screens/EventScreen"
import { BattleScreen } from "./screens/BattleScreen"
import { DeathScreen } from "./screens/DeathScreen"
import { VictoryScreen } from "./screens/VictoryScreen"
import { JournalScreen } from "./screens/JournalScreen"
import { SkillsScreen } from "./screens/SkillsScreen"
import { SpiritScreen } from "./screens/SpiritScreen"
import { HistoryScreen } from "./screens/HistoryScreen"
import { SettingsScreen } from "./screens/SettingsScreen"
import "./App.css"

type Screen =
  | "mainMenu"
  | "avatarSelect"
  | "worldMap"
  | "location"
  | "army"
  | "factions"
  | "event"
  | "trial"
  | "battle"
  | "death"
  | "victory"
  | "journal"
  | "skills"
  | "schedule"
  | "spirit"
  | "history"
  | "settings"

/** Every Screen a nav entry can reach, so the nav can highlight the current one. */
const NAV_TARGETS: NavTarget[] = [
  "worldMap",
  "location",
  "schedule",
  "journal",
  "skills",
  "army",
  "factions",
  "spirit",
  "history",
]

/** What the world did while the player was somewhere else. */
interface Digest {
  day: number
  reports: string[]
}

const SPIRIT_MET_KNOWLEDGE = "met_the_spirit"
const SPOKEN_WITH_SPIRIT_THIS_LOOP_FLAG = "spoken_with_spirit_this_loop"
const ORC_SIGN_RUMOURS_KNOWLEDGE = "heard_orc_sign_rumours"
const ORC_WARBAND_SIGHTING_KNOWLEDGE = "heard_orc_warband_sighting"
const GOLDENFIELDS_RUMOURS_KNOWLEDGE = "heard_goldenfields_rumours"
const GOLDENFIELDS_RIVERGATE_WARNING_KNOWLEDGE = "heard_goldenfields_warning_rivergate"
const IRONWATCH_SCOUT_RUMOURS_KNOWLEDGE = "heard_orc_muster_rumours"
const BLACKTOOTH_SCOUTED_KNOWLEDGE = "scouted_blacktooth_wastes"

interface DialogueContext {
  dialogueId: string
  returnScreen: Screen
  /** True when the player deliberately re-opened a conversation to re-read it,
   * so the auto-skip-seen-dialogue setting leaves it alone. */
  replay?: boolean
}

/** A long action waiting on the player's confirmation (see ConfirmDialog). */
interface PendingConfirm {
  title: string
  body: string
  confirmLabel: string
  onConfirm: () => void
}

interface PendingTraining {
  abilityId: string
  level: Exclude<AbilityLevel, "none">
}

const AUTOSAVE_HINT = "Progress autosaves."

function App() {
  const [gameState, setGameStateRaw] = useState<GameState | null>(null)
  // Every Phase 6 spirit trigger is derived from whole state rather than
  // reported by the handler that caused it, so routing every write through
  // here means no action can produce a discovery the spirit fails to notice.
  // Learning a road (Phase 7) opens locked routes, so discovery is flooded
  // here too — the places a newly-learned road reaches show up on the map
  // without waiting for the player to walk there first.
  function setGameState(next: GameState) {
    const withDiscovery: GameState = {
      ...next,
      permanent: discoverReachableLocations(next.permanent),
    }
    setGameStateRaw(recordSpiritDiscoveries(withDiscovery))
  }
  const [screen, setScreen] = useState<Screen>("mainMenu")
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null,
  )
  // The far end of a multi-leg journey the player is part way through. Travel
  // is taken one leg at a time (so events along the road still fire), which
  // used to drop the player at each waystation with no memory of where they
  // were headed — they had to reopen the map and re-pick the destination at
  // every stop. Holding the destination here lets a waystation offer to carry
  // the journey onward. It is transient UI intent, not saved state: choosing
  // any other road clears it, and it does not survive a reload.
  const [travelDestinationId, setTravelDestinationId] = useState<string | null>(
    null,
  )
  const [activeBattleId, setActiveBattleId] = useState<string | null>(null)
  // The accusation trial the player is currently facing, if any — Aldric's
  // forgery laid before the realm he framed this loop (see accusationEngine).
  const [activeTrial, setActiveTrial] = useState<AccusationTrial | null>(null)
  const [dialogueContext, setDialogueContext] = useState<DialogueContext | null>(
    null,
  )
  // Set while a battle-gated ability test (e.g. the Swordsmanship advanced
  // duel) is in progress, so handleBattleResolve knows to grant the level on
  // victory instead of treating it as an ordinary world-event battle.
  const [pendingTraining, setPendingTraining] = useState<PendingTraining | null>(
    null,
  )
  // A battle to enter once the current dialogue is dismissed, so an encounter
  // can be narrated before it is fought rather than after.
  const [battleAfterDialogue, setBattleAfterDialogue] = useState<string | null>(null)
  // Which of his moves is currently in play, so its result can be attributed
  // back to the right strategy (and the right garrison) when it resolves.
  const [antiPlayerContext, setAntiPlayerContext] = useState<{
    strategyId: string
    locationId: string
  } | null>(null)
  const [digest, setDigest] = useState<Digest | null>(null)
  // The loop number a turnover animation is currently playing for, or null.
  const [loopTransition, setLoopTransition] = useState<number | null>(null)
  const [saveStatus, setSaveStatus] = useState<string | null>(AUTOSAVE_HINT)
  const saveStatusTimer = useRef<number | null>(null)
  const { toasts, push, dismiss } = useToasts()
  const [settings] = useSettings()
  // A long action (a many-day march or training) waiting to be confirmed.
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null)

  // The ambient light of the whole UI is set from how far the loop has run
  // toward the Day 512 deadline: dawn at the start of a loop, sliding to an
  // extinguished-sun night as the deadline nears (see atmosphereEngine). The
  // phase is stamped on the document root so a single CSS layer can tint every
  // screen at once; cleared on the main menu, which stands outside the world.
  useEffect(() => {
    const root = document.documentElement
    if (!gameState || screen === "mainMenu") {
      delete root.dataset.timeofday
      root.style.removeProperty("--loop-darkness")
      return
    }
    // The coronation is the one moment the light comes back: the sun held, so
    // the whole UI holds at full day rather than the dusk/night the late day
    // counter would otherwise tint it.
    const restored = screen === "victory"
    root.dataset.timeofday = restored
      ? "day"
      : getTimeOfDay(gameState.loop.currentDay)
    // The room's light drains continuously as the loop runs toward Day 512 and
    // the sun the ritual is putting out: a black wash that is nothing on Day 1
    // and total by the deadline, easing a shade darker with each day that
    // passes. Held at nothing at the coronation, where the sun is saved.
    root.style.setProperty(
      "--loop-darkness",
      String(restored ? 0 : getLoopProgress(gameState.loop.currentDay)),
    )
  }, [gameState, screen])

  // The loop turning over is the one beat that deserves to be seen, not just
  // recorded. Watching the loop number rather than the ~three call sites that
  // raise it (Awaken, and any future reset) means the page-turn plays on every
  // path to a new loop. The very first loop (null -> 1, whether New Game or a
  // loaded save) is a start, not a turnover, so it is deliberately skipped.
  const prevLoopNumberRef = useRef<number | null>(null)
  useEffect(() => {
    const prev = prevLoopNumberRef.current
    const current = gameState?.loop.loopNumber ?? null
    prevLoopNumberRef.current = current
    if (prev !== null && current !== null && current > prev) {
      setLoopTransition(current)
    }
  }, [gameState?.loop.loopNumber])

  // Autosave after every action that changes the world. Losing a loop to a
  // closed tab is the harshest possible punishment in a game already built
  // around repeated death, and the save was previously manual and silent.
  // Battles are skipped because their turn state lives in the BattleScreen
  // component: the meaningful save point is the moment before the fight.
  useEffect(() => {
    if (!gameState) return
    if (screen === "battle" || screen === "mainMenu") return
    saveGame(gameState)
  }, [gameState, screen])

  // Announces the state changes the game used to make silently. Diffing whole
  // GameState here catches every path — the ~20 individual action handlers do
  // not each need to remember to report themselves.
  const previousState = useRef<GameState | null>(null)
  useEffect(() => {
    const prev = previousState.current
    previousState.current = gameState
    if (!gameState || !prev) return
    const loopNumber = gameState.loop.loopNumber
    const day = gameState.loop.currentDay

    // Death does not change the loop number (that waits for the next Awaken), so
    // it is observed here, before the reset guard, and recorded to the chronicle
    // and the play log the same way a gain is.
    if (prev.loop.alive && !gameState.loop.alive) {
      const sun = gameState.loop.worldFlags.includes("sun_extinguished")
      trackDeath(loopNumber, day, gameState.permanent.totalDeaths, sun ? "sun_extinguished" : "killed")
      trackLongestSurvival(gameState.permanent.longestSurvival)
      recordHistory(
        loopNumber,
        day,
        sun
          ? "The ritual completed and the sun was extinguished. The loop ended with the world."
          : "You died. The loop turns over.",
        "loss",
      )
    }

    // A new loop resets everything downward; those are not losses to report.
    if (loopNumber !== prev.loop.loopNumber) return

    // Passing the first month alive is the game's earliest real milestone, and
    // the point the spirit's story begins to open up.
    if (gameState.loop.alive && prev.loop.currentDay < 30 && day >= 30) {
      trackDay30Survival(loopNumber)
    }

    for (const id of gameState.permanent.knowledge) {
      if (prev.permanent.knowledge.includes(id)) continue
      if (id.startsWith("seen_")) continue
      if (id === "campaign_won") {
        trackGameCompleted(loopNumber, day, gameState.permanent.totalDeaths)
      }
      if (id.startsWith("resolved_")) {
        trackFactionCompletion(id.slice("resolved_".length), "resolved")
      }
      const entry = getJournalEntryByKnowledgeId(id)
      if (entry) {
        push(`Learned: ${entry.title}`, "gain")
        recordHistory(loopNumber, day, `Learned: ${entry.title}`, "gain")
      }
    }

    for (const [abilityId, level] of Object.entries(gameState.permanent.abilities)) {
      if ((prev.permanent.abilities[abilityId] ?? "none") === level) continue
      const name = getAbilityById(abilityId)?.name ?? abilityId
      push(`${name} is now ${level}.`, "gain")
      recordHistory(loopNumber, day, `${name} raised to ${level}.`, "milestone")
      trackAbilityProgression(abilityId, level)
    }

    const commandPointDelta = gameState.loop.commandPoints - prev.loop.commandPoints
    if (commandPointDelta > 0) {
      push(`+${commandPointDelta} Command Points.`, "gain")
    }

    const moneyDelta = gameState.loop.money - prev.loop.money
    if (moneyDelta > 0) push(`+${moneyDelta} coin.`, "gain")

    for (const faction of factions) {
      const before = getTrust(prev.loop, faction.id)
      const after = getTrust(gameState.loop, faction.id)
      if (TRUST_ORDER.indexOf(after) > TRUST_ORDER.indexOf(before)) {
        push(`${faction.name} now regard you as ${after}.`, "gain")
        recordHistory(loopNumber, day, `${faction.name} now regard you as ${after}.`, "milestone")
      }
    }

    for (const factionId of gameState.loop.subjectedFactions) {
      if (prev.loop.subjectedFactions.includes(factionId)) continue
      trackFactionCompletion(factionId, "subjected")
    }
  }, [gameState, push])

  // Delivers whatever the spirit owes the player, whenever it comes due.
  // Driving this from state rather than from the ~20 handlers that can cause a
  // discovery is what lets a scene fire on *any* path to it — the Day 30
  // revelation, a first master ability and a first meeting with the anti-player
  // are all just scenes whose conditions became true.
  useEffect(() => {
    if (!gameState || !gameState.loop.alive) return
    if (
      screen === "battle" ||
      screen === "event" ||
      screen === "trial" ||
      screen === "mainMenu" ||
      screen === "victory"
    ) {
      return
    }
    if (battleAfterDialogue) return
    const pending = getPendingSpiritScene(gameState.permanent)
    if (!pending) return
    setDialogueContext({ dialogueId: pending.dialogueId, returnScreen: screen })
    setScreen("event")
  }, [gameState, screen, battleAfterDialogue])

  useEffect(
    () => () => {
      if (saveStatusTimer.current !== null) {
        window.clearTimeout(saveStatusTimer.current)
      }
    },
    [],
  )

  // Planning a loop means constant round trips between the map, the schedule
  // and the journal, and every one of them was a trip to the sidebar with the
  // mouse. Number keys jump straight to a screen; Escape backs out to the map.
  // Screens that must not be abandoned mid-way (a battle, a dialogue, death)
  // are excluded, as is anything typed into a text field.
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.ctrlKey || event.metaKey || event.altKey) return
      // A confirmation dialog owns the keyboard while it is up (Enter/Escape),
      // so the nav's Escape-to-map and number keys stand aside for it.
      if (pendingConfirm) return
      const target = event.target as HTMLElement | null
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) {
        return
      }
      if (screen === "battle" || screen === "event" || screen === "death") return
      if (screen === "trial" || screen === "victory") return
      if (screen === "mainMenu" || !gameState?.loop.alive) return

      if (event.key === "Escape") {
        event.preventDefault()
        setScreen("worldMap")
        return
      }
      const index = Number(event.key)
      if (!Number.isInteger(index) || index < 1 || index > NAV_ITEMS.length) return
      event.preventDefault()
      handleNavigate(NAV_ITEMS[index - 1].target)
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [screen, gameState?.loop.alive, pendingConfirm])

  function openDialogue(dialogueId: string, returnScreen: Screen, replay = false) {
    setDialogueContext({ dialogueId, returnScreen, replay })
    setScreen("event")
  }

  /**
   * Runs an action that spends `days`, first asking the player to confirm it if
   * the confirm-long-actions setting is on and the cost meets their threshold.
   * Everything short of that threshold — a rest, a single leg — proceeds without
   * a prompt, so only the genuinely expensive commitments interrupt the flow.
   */
  function runWithLongActionGuard(days: number, label: string, proceed: () => void) {
    if (!settings.confirmLongActions || days < settings.longActionThreshold) {
      proceed()
      return
    }
    setPendingConfirm({
      title: `Spend ${days} days?`,
      body: `${label} will take ${days} days, and days are the one thing you cannot get back. The world will not wait while you are busy.`,
      confirmLabel: `Yes — spend ${days} days`,
      onConfirm: () => {
        setPendingConfirm(null)
        proceed()
      },
    })
  }

  /**
   * The spirit only speaks once per loop — at the start of it. That first
   * conversation (triggered automatically on New Game/Awaken) marks this
   * flag, which then keeps the "Speak With the Spirit" nav option disabled
   * for the rest of the loop.
   */
  function openSpiritDialogue(state: GameState) {
    if (state.loop.worldFlags.includes(SPOKEN_WITH_SPIRIT_THIS_LOOP_FLAG)) return
    setGameState(setWorldFlag(state, SPOKEN_WITH_SPIRIT_THIS_LOOP_FLAG))
    openDialogue(
      state.permanent.knowledge.includes(SPIRIT_MET_KNOWLEDGE)
        ? "spirit_intro_return"
        : "spirit_intro",
      "location",
    )
  }

  /**
   * Shared by every action that advances the calendar (Rest, Travel,
   * Training, the World Map's Advance Day button, investigating ruins):
   * applies the resulting state, and routes to the Battle screen if a
   * forced encounter (e.g. the Day 2 Dawnmere attack) just triggered.
   * Otherwise, if this action just revealed the Day 512 deadline for the
   * first time, the spirit interrupts with the Day 30 revelation before
   * handing off to onSettled/fallbackScreen.
   */
  function afterStateChange(
    prev: GameState,
    next: GameState,
    fallbackScreen: Screen,
    onSettled?: () => void,
  ) {
    const { battleId, flags, reports } = resolveTriggeredEvents(prev, next)
    const withFlags = flags.reduce((state, flag) => setWorldFlag(state, flag), next)

    // A scheduled world event takes the day: he does not walk his hired men
    // into somebody else's battle. His move is simply missed for this loop,
    // which is a real (if accidental) way for the player to dodge one.
    const antiOutcome = battleId ? null : resolveAntiPlayerMove(prev, withFlags)
    const afterAnti = antiOutcome
      ? applyAntiPlayerOutcome(withFlags, antiOutcome)
      : withFlags

    // Day 512 reached with the Necromancer still alive: the ritual completes,
    // the sun goes out, and the loop ends — not by death, but by the world's.
    // This is the "one loop reaching the Necromancer too late" the campaign is
    // meant to allow, so it turns the loop over the same way a death does.
    if (flags.includes("sun_extinguished")) {
      setGameState(killPlayer(afterAnti))
      setScreen("death")
      return
    }

    setGameState(afterAnti)
    // Anything that happened somewhere the player was not standing gets
    // reported before they act again, rather than being left to be discovered
    // days later by walking into the aftermath.
    const allReports = [
      ...reports,
      ...(antiOutcome?.report ? [antiOutcome.report] : []),
    ]
    if (allReports.length > 0) {
      setDigest({ day: afterAnti.loop.currentDay, reports: allReports })
      // The digest is fleeting; the chronicle keeps what it said.
      recordHistoryMany(
        afterAnti.loop.loopNumber,
        afterAnti.loop.currentDay,
        allReports.map((text) => ({ text, kind: "world" as const })),
      )
    }
    if (battleId) {
      setActiveBattleId(battleId)
      setScreen("battle")
      return
    }
    // Meeting him is narrated first, then fought — the whole point of him is
    // what he says while the hired men are closing in.
    if (antiOutcome?.encountered && antiOutcome.dialogueId) {
      setAntiPlayerContext({
        strategyId: antiOutcome.strategy.id,
        locationId: antiOutcome.locationId,
      })
      setBattleAfterDialogue(antiOutcome.battleId)
      openDialogue(antiOutcome.dialogueId, fallbackScreen)
      return
    }
    // Walking into the seat of the realm he has framed this loop: the court is
    // waiting with the forged evidence, and there is no settling back to the
    // location screen until the accusation has been faced.
    const pendingTrial = getPendingTrial(afterAnti)
    if (pendingTrial) {
      setActiveTrial(pendingTrial)
      setScreen("trial")
      return
    }
    if (onSettled) {
      onSettled()
      return
    }
    setScreen(fallbackScreen)
  }

  /**
   * Folds one of his moves into state: the flags it leaves behind, the
   * knowledge it teaches, the encounter it adds to his memory, and — when a
   * garrison was given orders in the player's name and marched off — the
   * units that are simply no longer there.
   */
  function applyAntiPlayerOutcome(
    state: GameState,
    outcome: ReturnType<typeof resolveAntiPlayerMove>,
  ): GameState {
    if (!outcome) return state
    const withFlags = outcome.flags.reduce(
      (current, flag) => setWorldFlag(current, flag),
      state,
    )
    const withKnowledge = grantKnowledge(withFlags, outcome.knowledge)
    const withEncounter = outcome.encountered
      ? { ...withKnowledge, permanent: recordEncounter(withKnowledge.permanent) }
      : withKnowledge
    if (!outcome.flags.includes("anti_player_turned_a_garrison")) {
      return withEncounter
    }
    return destroyGarrisonAt(withEncounter, outcome.locationId)
  }

  /** New Game first asks which face the player will wear before the world begins. */
  function handleNewGame() {
    setScreen("avatarSelect")
  }

  // Returning to the menu has to drop the in-memory game, not just change the
  // screen. The whole pre-game guard chain (settings, avatar select, and the
  // "no game yet" fall-through) is written against the invariant that no
  // GameState exists on the menu; leaving a loaded game in place made New Game
  // fall through to the still-loaded world, so it looked exactly like Continue.
  // The save in localStorage is untouched, so Continue can still resume it.
  function handleReturnToMainMenu() {
    setGameStateRaw(null)
    setSelectedLocationId(null)
    setTravelDestinationId(null)
    setActiveTrial(null)
    setDialogueContext(null)
    setScreen("mainMenu")
  }

  function handleChooseAvatar(avatarIndex: number) {
    const created = createNewGame()
    const next: GameState = {
      ...created,
      permanent: { ...created.permanent, avatarIndex },
    }
    trackLoopStarted(next.loop.loopNumber, "new_game")
    recordHistory(next.loop.loopNumber, next.loop.currentDay, "You wake in Dawnmere. A new loop begins.", "milestone")
    setGameState(next)
    setSelectedLocationId(null)
    setTravelDestinationId(null)
    openSpiritDialogue(next)
  }

  function handleContinue() {
    const loaded = loadGame()
    if (!loaded) return
    setGameState(loaded)
    setTravelDestinationId(null)
    setScreen(loaded.loop.alive ? "worldMap" : "death")
  }

  function handleSelectLocation(locationId: string) {
    setSelectedLocationId(locationId)
    setScreen("location")
  }

  function handleAdvanceDay() {
    if (!gameState) return
    afterStateChange(gameState, advanceDay(gameState), "worldMap")
  }

  function handleRest() {
    if (!gameState) return
    afterStateChange(gameState, advanceDay(gameState, 1), "location")
  }

  function handleSave() {
    if (!gameState) return
    saveGame(gameState)
    // Saving used to give no sign it had worked at all.
    setSaveStatus("Saved.")
    if (saveStatusTimer.current !== null) window.clearTimeout(saveStatusTimer.current)
    saveStatusTimer.current = window.setTimeout(() => setSaveStatus(AUTOSAVE_HINT), 3000)
  }

  function handleNavigate(target: NavTarget) {
    if (target === "location") setSelectedLocationId(null)
    setScreen(target)
  }

  // `finalDestinationId` is the far end of a multi-leg journey, if this leg is
  // one of several. It is remembered so the waystation this leg ends at can
  // offer to carry the journey onward; omitting it (any single-hop travel)
  // clears any journey in progress, which is how "go some other way" cancels a
  // scheduled trip.
  function handleTravel(routeId: string, finalDestinationId?: string) {
    if (!gameState) return
    // Closed borders (Phase 4 trust-gated locations) make travel throw. The
    // UI already disables those routes, so treat a throw as a no-op rather
    // than tearing down the app.
    const route = getRouteById(routeId)
    const destinationId =
      route?.fromLocationId === gameState.loop.currentLocationId
        ? route?.toLocationId
        : route?.fromLocationId
    if (destinationId && getTravelBlockReason(gameState.loop, destinationId)) return
    const destinationName = destinationId
      ? getLocationById(destinationId)?.name ?? "there"
      : "there"
    const days = route?.travelDays ?? 0
    runWithLongActionGuard(days, `Setting out for ${destinationName}`, () => {
      if (!gameState) return
      setSelectedLocationId(null)
      const next = travel(gameState, routeId)
      // Keep tracking the journey only while there is still road left to it;
      // once this leg lands the player at the final destination, it is done.
      setTravelDestinationId(
        finalDestinationId && finalDestinationId !== next.loop.currentLocationId
          ? finalDestinationId
          : null,
      )
      afterStateChange(gameState, next, "location")
    })
  }

  /** The player abandons a journey in progress; the road is theirs to choose again. */
  function handleCancelJourney() {
    setTravelDestinationId(null)
  }

  function handleTrainAbility(
    abilityId: string,
    level: Exclude<AbilityLevel, "none">,
  ) {
    if (!gameState) return
    const ability = getAbilityById(abilityId)
    const levelDefinition = ability?.levels.find((l) => l.level === level)
    const days = levelDefinition?.trainingDays ?? 0
    runWithLongActionGuard(days, `Training ${ability?.name ?? abilityId} (${level})`, () => {
      if (!gameState) return
      const trained = trainAbility(gameState, abilityId, level)
      const testBattleId = levelDefinition?.testBattleId
      if (testBattleId) {
        // Training days are already spent; the level itself is granted only
        // after the test battle is won (see handleBattleResolve).
        afterStateChange(gameState, trained, "location", () => {
          setPendingTraining({ abilityId, level })
          setActiveBattleId(testBattleId)
          setScreen("battle")
        })
        return
      }
      afterStateChange(gameState, trained, "location", () =>
        openDialogue(`training_${abilityId}_${level}`, "location"),
      )
    })
  }

  function handleOpenDialogue(dialogueId: string) {
    openDialogue(dialogueId, "location")
  }

  /**
   * A claim of foreknowledge alone never changes anyone's behaviour (see
   * GAME_CREATION_INSTRUCTIONS.md) — the guard only mobilizes if the player
   * also has corroborating evidence gathered this loop or a prior one.
   */
  function hasEvidenceForWarning(state: GameState): boolean {
    return (
      state.permanent.knowledge.includes(ORC_SIGN_RUMOURS_KNOWLEDGE) ||
      state.permanent.knowledge.includes(ORC_WARBAND_SIGHTING_KNOWLEDGE)
    )
  }

  function handleWarnGuard() {
    if (!gameState) return
    if (hasEvidenceForWarning(gameState)) {
      setGameState(setWorldFlag(gameState, "dawnmere_soldiers_convinced"))
      openDialogue("dawnmere_guard_warned", "location")
    } else {
      openDialogue("dawnmere_guard_disbelieved", "location")
    }
  }

  function handleEvacuate() {
    if (!gameState) return
    if (hasEvidenceForWarning(gameState)) {
      setGameState(setWorldFlag(gameState, "dawnmere_evacuated"))
      openDialogue("dawnmere_evacuate", "location")
    } else {
      openDialogue("dawnmere_evacuate_disbelieved", "location")
    }
  }

  /** Investigating the ruins takes a day, like Rest, but then opens its dialogue instead of returning straight to the location. */
  function handleInvestigateRuins() {
    if (!gameState) return
    afterStateChange(gameState, advanceDay(gameState, 1), "location", () =>
      openDialogue("dawnmere_ruins_investigation", "location"),
    )
  }

  function hasGoldenfieldsEvidence(state: GameState): boolean {
    return (
      state.permanent.knowledge.includes(GOLDENFIELDS_RUMOURS_KNOWLEDGE) ||
      state.permanent.knowledge.includes(GOLDENFIELDS_RIVERGATE_WARNING_KNOWLEDGE)
    )
  }

  function handleRallyGoldenfieldsMilitia() {
    if (!gameState) return
    if (hasGoldenfieldsEvidence(gameState)) {
      setGameState(setWorldFlag(gameState, "goldenfields_militia_rallied"))
      openDialogue("goldenfields_reeve_warned", "location")
    } else {
      openDialogue("goldenfields_reeve_disbelieved", "location")
    }
  }

  function handleEvacuateGoldenfields() {
    if (!gameState) return
    if (hasGoldenfieldsEvidence(gameState)) {
      setGameState(setWorldFlag(gameState, "goldenfields_evacuated"))
      openDialogue("goldenfields_evacuate", "location")
    } else {
      openDialogue("goldenfields_evacuate_disbelieved", "location")
    }
  }

  /** Only available once evidence has been gathered from both Goldenfields and Rivergate — see LocationScreen's gating. */
  function handleSendWordToCrownreach() {
    if (!gameState) return
    setGameState(setWorldFlag(gameState, "goldenfields_raid_prevented"))
    openDialogue("goldenfields_send_word_crownreach", "location")
  }

  function handleWarnIronwatchGarrison() {
    if (!gameState) return
    const hasEvidence =
      gameState.permanent.knowledge.includes(IRONWATCH_SCOUT_RUMOURS_KNOWLEDGE) ||
      gameState.permanent.knowledge.includes(BLACKTOOTH_SCOUTED_KNOWLEDGE)
    if (hasEvidence) {
      setGameState(setWorldFlag(gameState, "ironwatch_raid_prevented"))
      openDialogue("ironwatch_garrison_warned", "location")
    } else {
      openDialogue("ironwatch_garrison_disbelieved", "location")
    }
  }

  function handleSeekCrownAudience() {
    if (!gameState) return
    setGameState(raiseFactionTrust(gameState, "humans", "recognized"))
    openDialogue("crownreach_audience_granted", "location")
  }

  // Proving a noble house's founding was forged is a service the crown notices.
  // It earns the same first rung of standing as a formal audience — a
  // scholar's route to recognition for a player who has read more than they
  // have negotiated. raiseFactionTrust only ever raises, so a player already
  // further along the ladder loses nothing by exposing the lie.
  function handleExposeFalseHistory() {
    if (!gameState) return
    setGameState(raiseFactionTrust(gameState, "humans", "recognized"))
    openDialogue("crownreach_expose_false_history", "location")
  }

  // Spends this loop's looted coin on the next suit of armour, which — unlike
  // the coin — is kept for good (see loopEngine.buyArmor). A no-op the button is
  // already guarding against (unaffordable / best suit owned), so it is safe to
  // call unconditionally.
  function handleBuyArmor() {
    if (!gameState) return
    setGameState(buyArmor(gameState))
  }

  function handlePledgeServiceToCrown() {
    if (!gameState) return
    const withTrust = raiseFactionTrust(gameState, "humans", "trusted")
    const withUnits = grantUnitsAtLocation(
      withTrust,
      "ironwatch_fortress",
      "kingdom_infantry",
      10 * ARMY_SCALE,
    )
    setGameState(withUnits)
    openDialogue("crownreach_pledge_service", "location")
  }

  function handleAcceptCommandOfRealm() {
    if (!gameState) return
    const withTrust = raiseFactionTrust(gameState, "humans", "subjected")
    const withSubjection = subjectFaction(withTrust, "humans")
    const withUnits = grantUnitsAtLocation(
      withSubjection,
      "ironwatch_fortress",
      "human_crossbowmen",
      10 * ARMY_SCALE,
    )
    setGameState(withUnits)
    openDialogue("crownreach_crown_subjected", "location")
  }

  /**
   * The Crown calls in the realms that owe it a debt. Each contingent musters
   * where it is closest to being useful rather than all in one place, so
   * fielding a mixed army is a matter of planning a route, not clicking a
   * button — and the counters those units represent (dwarves against giants,
   * Flame Guard against trolls, Ritual Mages against anchors, Pathfinders
   * against Dark Elves, Spirit Wardens against the dead) have to be carried
   * to the battle that needs them.
   */
  function handleCallTheRealmsToMuster() {
    if (!gameState) return
    const contingents: Array<[string, string, number]> = [
      ["ironwatch_fortress", "dwarven_shieldbearers", 12],
      ["ironwatch_fortress", "dwarven_hammerguard", 8],
      ["rivergate", "flame_guard", 12],
      ["rivergate", "ritual_mages", 10],
      ["goldenfields", "wood_elf_pathfinders", 10],
      ["ruined_temple_first_dawn", "spirit_wardens", 10],
    ]
    const next = contingents.reduce(
      (state, [locationId, unitId, count]) =>
        grantUnitsAtLocation(state, locationId, unitId, count * ARMY_SCALE),
      grantCommandPoints(setWorldFlag(gameState, "realms_mustered"), 12 * ARMY_SCALE),
    )
    setGameState(next)
    openDialogue("crownreach_call_the_realms", "location")
  }

  function handleMeetAcademyInstructors() {
    if (!gameState) return
    setGameState(raiseFactionTrust(gameState, "high_elves", "recognized"))
    openDialogue("starbow_academy_meeting", "location")
  }

  function handleSeekElvenCourtAudience() {
    if (!gameState) return
    const withTrust = raiseFactionTrust(gameState, "high_elves", "trusted")
    const withUnits = grantUnitsAtLocation(
      withTrust,
      "starbow_academy",
      "high_elf_archers",
      8 * ARMY_SCALE,
    )
    setGameState(withUnits)
    openDialogue("moonspire_court_audience", "location")
  }

  function handleOfferFealtyToElvenCrown() {
    if (!gameState) return
    const withTrust = raiseFactionTrust(gameState, "high_elves", "subjected")
    const withSubjection = subjectFaction(withTrust, "high_elves")
    const withUnits = grantUnitsAtLocation(
      withSubjection,
      "starbow_academy",
      "high_elf_lancers",
      8 * ARMY_SCALE,
    )
    setGameState(withUnits)
    openDialogue("moonspire_subjected", "location")
  }

  /**
   * The single handler behind every Phase 7 faction action (see questEngine).
   * It applies the action's declared effects, spends its days if it takes any
   * (routing through afterStateChange so events and the anti-player still
   * resolve along the way, exactly like Rest or Training), then shows its
   * dialogue — and, for a decisive assault or duel, hands off to the battle
   * once that dialogue is dismissed.
   */
  function handleFactionAction(actionId: string) {
    if (!gameState) return
    const action = getFactionActionById(actionId)
    if (!action) return
    const withEffects = applyFactionActionEffects(gameState, action)
    const proceed = () => {
      if (action.startBattle) setBattleAfterDialogue(action.startBattle)
      openDialogue(action.dialogueId, "location")
    }
    // Phase 9: content already mastered in an earlier loop costs less to repeat.
    const costDays = getFactionActionCostDays(action, gameState.permanent)
    if (costDays > 0) {
      runWithLongActionGuard(costDays, action.label, () => {
        if (!gameState) return
        afterStateChange(gameState, advanceDay(withEffects, costDays), "location", proceed)
      })
      return
    }
    setGameState(withEffects)
    proceed()
  }

  function handleTakeUnit(unitId: string, count: number = 1) {
    if (!gameState) return
    const { loop } = gameState
    const available =
      getStacksAtLocation(loop, loop.currentLocationId).find((s) => s.unitId === unitId)
        ?.count ?? 0
    const toTake = Math.min(available, Math.max(count, 0))
    if (toTake <= 0) return
    setGameState(takeUnitsAtLocation(gameState, loop.currentLocationId, unitId, toTake))
  }

  function handleTakeAllOfUnit(unitId: string) {
    if (!gameState) return
    const { loop } = gameState
    const available =
      getStacksAtLocation(loop, loop.currentLocationId).find((s) => s.unitId === unitId)
        ?.count ?? 0
    const remainingCommandPoints = loop.commandPoints - commandPointsUsed(loop.currentArmy)
    const toTake = Math.min(available, Math.max(remainingCommandPoints, 0))
    if (toTake <= 0) return
    setGameState(takeUnitsAtLocation(gameState, loop.currentLocationId, unitId, toTake))
  }

  function handleTakeAll() {
    if (!gameState) return
    setGameState(takeAllUnitsAtLocation(gameState, gameState.loop.currentLocationId))
  }

  function handleLeaveUnit(unitId: string) {
    if (!gameState) return
    setGameState(leaveUnitsAtLocation(gameState, gameState.loop.currentLocationId, unitId, 1))
  }

  function handleLeaveAllOfUnit(unitId: string) {
    if (!gameState) return
    const inArmy =
      gameState.loop.currentArmy.find((s) => s.unitId === unitId)?.count ?? 0
    if (inArmy <= 0) return
    setGameState(
      leaveUnitsAtLocation(gameState, gameState.loop.currentLocationId, unitId, inArmy),
    )
  }

  function handleReleaseUnit(unitId: string) {
    if (!gameState) return
    setGameState(releaseUnitsFromArmy(gameState, unitId, 1))
  }

  function handleReleaseAllOfUnit(unitId: string) {
    if (!gameState) return
    const inArmy =
      gameState.loop.currentArmy.find((s) => s.unitId === unitId)?.count ?? 0
    if (inArmy <= 0) return
    setGameState(releaseUnitsFromArmy(gameState, unitId, inArmy))
  }

  function handleEventContinue(grantedKnowledge: string[]) {
    if (!gameState) return
    const withKnowledge = grantKnowledge(gameState, grantedKnowledge)
    // A spirit scene is only ever owed once. Marking it here rather than when
    // it opens means a scene interrupted by a closed tab is still owed.
    const sceneId = dialogueContext
      ? getSceneIdForDialogue(dialogueContext.dialogueId)
      : null
    const next = sceneId
      ? { ...withKnowledge, permanent: markSpiritSceneSeen(withKnowledge.permanent, sceneId) }
      : withKnowledge
    setGameState(next)

    if (battleAfterDialogue) {
      setActiveBattleId(battleAfterDialogue)
      setBattleAfterDialogue(null)
      setScreen("battle")
      return
    }
    setScreen(dialogueContext?.returnScreen ?? "worldMap")
  }

  /**
   * Putting a *new* question to the spirit costs one of the loop's audiences;
   * re-reading an answer already earned costs nothing, in this loop or any
   * later one. Knowledge paid for once should never have to be bought twice.
   */
  function handleAskSpiritQuestion(questionId: string) {
    if (!gameState) return
    const question = spiritQuestions.find((candidate) => candidate.id === questionId)
    if (!question) return
    const alreadyAsked = gameState.permanent.spiritQuestionsAsked.includes(questionId)
    if (alreadyAsked) {
      openDialogue(question.dialogueId, "spirit", true)
      return
    }
    setGameState({
      permanent: markSpiritQuestionAsked(gameState.permanent, questionId),
      loop: consumeSpiritAudience(gameState.loop),
    })
    openDialogue(question.dialogueId, "spirit")
  }

  /**
   * The temple's lower vault names him — but only to someone with the
   * scholarship to read a pre-kingdom register and a reason to know there is a
   * name worth looking for. It also holds the one piece of evidence that
   * refutes him, which is what makes the severance possible at all.
   */
  function handleSearchTempleRecords() {
    if (!gameState) return
    afterStateChange(gameState, advanceDay(gameState, 1), "location", () => {
      setGameStateRaw((current) =>
        current
          ? recordSpiritDiscoveries({
              ...current,
              permanent: discoverIdentity(current.permanent),
            })
          : current,
      )
      openDialogue("anti_player_temple_records", "location")
    })
  }

  function handleFaceAntiPlayer() {
    if (!gameState) return
    setAntiPlayerContext({
      strategyId: "final_reckoning",
      locationId: gameState.loop.currentLocationId,
    })
    setActiveBattleId(FINAL_BATTLE_ID)
    setScreen("battle")
  }

  /**
   * Storming an orc-held Ironwatch back into the kingdom's hands. The player's
   * marching army does the fighting; on victory handleBattleResolve lifts the
   * "controlled_by_enemy" scar (see the control-flip there), which reopens the
   * fortress and frees any garrison left waiting inside when it fell.
   */
  function handleRetakeIronwatch() {
    if (!gameState) return
    setActiveBattleId("ironwatch_retake_battle")
    setScreen("battle")
  }

  /**
   * The morally clean resolution. It is not a mercy the player extends — it is
   * an argument they win, with evidence they had to dig up and standing they
   * had to earn, against a man who has correctly disbelieved a century of
   * comforting lies.
   */
  function handleOfferSeverance() {
    if (!gameState) return
    setGameState({
      ...gameState,
      permanent: severAntiPlayer(gameState.permanent),
    })
    openDialogue("anti_player_severance", "location")
  }

  /**
   * A wrong answer at the trial: the court calls the household guard. The
   * arrest battle is lethal — losing it is a hanging (its defeatFlags carry
   * `executed_by_accusation`, which the DeathScreen reads). Winning or fleeing
   * leaves the player alive but branded, the alliance lost for the loop, which
   * is how the rival wins a run: the trial's faced/cut-free flags come off the
   * battle itself on victory or retreat.
   */
  function handleTrialArrested() {
    if (!gameState || !activeTrial) return
    const battleId = getAccusationBattleId(activeTrial.factionId)
    setActiveTrial(null)
    if (!battleId) {
      setScreen("location")
      return
    }
    setActiveBattleId(battleId)
    setScreen("battle")
  }

  /**
   * Every step answered truthfully: the forgery is disproved. The realm is
   * recorded as faced and cleared this loop (which lifts the alliance block in
   * isAllianceSabotaged), and the player keeps a permanent note that they have
   * broken this realm's forgery before — a record of foreknowledge for the
   * loops to come.
   */
  function handleTrialCleared() {
    if (!gameState || !activeTrial) return
    const factionId = activeTrial.factionId
    const clearedKnowledge = getClearedKnowledge(factionId)
    let next = setWorldFlag(gameState, accusationFacedFlag(factionId))
    next = clearAccusation(next, factionId)
    if (clearedKnowledge) next = grantKnowledge(next, [clearedKnowledge])
    setGameState(next)
    setActiveTrial(null)
    setScreen("location")
  }

  function handleSimulateDeath() {
    if (!gameState) return
    setGameState(killPlayer(gameState))
    setScreen("death")
  }

  function handleAwaken() {
    if (!gameState) return
    const next = startNewLoop(gameState)
    trackLoopStarted(next.loop.loopNumber, "awaken")
    recordHistory(next.loop.loopNumber, next.loop.currentDay, "You wake in Dawnmere. The wheel turns again.", "milestone")
    setGameState(next)
    setSelectedLocationId(null)
    setTravelDestinationId(null)
    openSpiritDialogue(next)
  }

  /**
   * After the coronation. The campaign is won and the player was never killed,
   * so startNewLoop's "must be dead first" guard does not apply — a fresh loop
   * is begun directly, permanent progress (including the win itself) intact,
   * for anyone who wants to run the world again.
   */
  function handleBeginAgainAfterVictory() {
    if (!gameState) return
    const next: GameState = {
      permanent: gameState.permanent,
      loop: createNewLoop(gameState.loop.loopNumber + 1, gameState.permanent),
    }
    trackLoopStarted(next.loop.loopNumber, "after_victory")
    recordHistory(next.loop.loopNumber, next.loop.currentDay, "You choose to walk the world again, the crown already won.", "milestone")
    setGameState(next)
    setSelectedLocationId(null)
    setTravelDestinationId(null)
    openSpiritDialogue(next)
  }

  /**
   * Beating one of his methods is the only thing that moves him on to the next
   * one — that is the whole of his adaptation, and it is why the player sees
   * his tactics escalate across loops rather than within one.
   *
   * A won fight is also the only time he will talk. With enough standing to be
   * worth talking to, and a name already dug out of the temple records, he
   * explains what he actually wants — which is the second of the two things
   * the severance requires.
   */
  function handleAntiPlayerVictory(state: GameState, battleId: string) {
    const strategy = getStrategyForBattle(battleId)
    const methodId = strategy?.id ?? "final_reckoning"
    let permanent = recordMethodFailed(state.permanent, methodId)

    const canTalk =
      permanent.antiPlayerState.identityDiscovered &&
      !permanent.antiPlayerState.motivationDiscovered &&
      isAtLeastLevel(getAbilityLevel(permanent, "diplomacy"), "advanced")
    if (canTalk) permanent = discoverMotivation(permanent)

    setGameState({ ...state, permanent })
    setAntiPlayerContext(null)
    setPendingTraining(null)

    if (canTalk) {
      openDialogue("anti_player_motivation_revealed", "location")
      return
    }
    const victoryDialogueId = `${battleId}_victory`
    if (getDialogueById(victoryDialogueId)) {
      openDialogue(victoryDialogueId, "location")
      return
    }
    setScreen("location")
  }

  function handleBattleResolve(resolution: BattleResolution) {
    if (!gameState) return
    const { outcome, flags, survivingPlayerArmy, lootMoney, commandPointReward } =
      resolution
    const battle = activeBattleId ? getBattleById(activeBattleId) : undefined

    // Feeds the battle-failure-rate metric, and the chronicle line for the fight.
    if (activeBattleId) trackBattleResolved(activeBattleId, outcome)
    if (battle && outcome !== "victory") {
      const line =
        outcome === "retreat"
          ? `You withdrew from ${battle.name}, giving up the field.`
          : `You were defeated at ${battle.name}.`
      recordHistory(gameState.loop.loopNumber, gameState.loop.currentDay, line, "battle")
    } else if (battle) {
      recordHistory(
        gameState.loop.loopNumber,
        gameState.loop.currentDay,
        `You won the field at ${battle.name}.`,
        "battle",
      )
    }

    // Healing turns some of a won battle's casualties back into survivors —
    // the walking wounded who would otherwise have been written off.
    const recoveredArmy =
      outcome === "victory"
        ? applyHealingRecovery(
            gameState.loop.currentArmy,
            survivingPlayerArmy,
            getPlayerBattleModifiers(gameState.permanent).casualtyRecoveryRate,
          )
        : survivingPlayerArmy

    // Non-lethal battles are training trials the player fights alone (see the
    // duelist stack on the battle itself), so the escort never entered the
    // fight and must not take casualties from it.
    const withCasualties = battle?.nonLethal
      ? gameState
      : applyBattleCasualties(gameState, recoveredArmy)
    const withFlags = flags.reduce(
      (state, flag) => setWorldFlag(state, flag),
      withCasualties,
    )
    // Retaking a stronghold flips it from enemy to player control. The victory
    // flag records the win, but the "controlled_by_enemy" scar it supersedes
    // has to be lifted by hand, since flags are otherwise only ever added —
    // and lifting it is what reopens the location's actions and frees any army
    // that was left waiting inside when it fell.
    const withControlFlipped = flags
      .filter((flag) => flag.endsWith("_controlled_by_player"))
      .reduce(
        (state, flag) =>
          clearWorldFlag(
            state,
            flag.replace(/_controlled_by_player$/, "_controlled_by_enemy"),
          ),
        withFlags,
      )
    // Facing a hostile faction for the first time is one of the spirit's
    // triggers, and the battle is the only record that it happened.
    const withEncounters = activeBattleId
      ? grantKnowledge(withControlFlipped, getEnemyFactionDiscoveries(activeBattleId))
      : withControlFlipped
    // Losing the field to him can also mean losing the garrison he talked into
    // marching away — the flag alone would be narration without this.
    const withLosses =
      flags.includes("anti_player_turned_a_garrison") && antiPlayerContext
        ? destroyGarrisonAt(withEncounters, antiPlayerContext.locationId)
        : withEncounters

    // Withdrawing is a loss of the field, not of your life: the world records
    // that the ground was given up, but the loop continues.
    if (outcome === "retreat") {
      setGameState(withLosses)
      setPendingTraining(null)
      setAntiPlayerContext(null)
      setScreen("location")
      return
    }

    if (outcome === "defeat") {
      // Ability test duels are training exercises, not mortal combat — a
      // loss just means the test wasn't passed yet, not death.
      if (battle?.nonLethal) {
        setGameState(withLosses)
        setPendingTraining(null)
        setAntiPlayerContext(null)
        setScreen("location")
        return
      }
      setGameState(killPlayer(withLosses))
      setPendingTraining(null)
      setAntiPlayerContext(null)
      setScreen("death")
      return
    }

    // A won field puts its survivors, prisoners and lent contingents under the
    // player's banner: Command Points for the rest of the loop, sized by the
    // same ARMY_SCALE knob every other Command Point source uses. The +N toast
    // is raised by the state-diff effect above, so granting is all that's needed.
    const commanded =
      commandPointReward > 0
        ? grantCommandPoints(withLosses, commandPointReward * ARMY_SCALE)
        : withLosses
    const looted = lootMoney > 0 ? addMoney(commanded, lootMoney) : commanded

    // The end of the whole campaign: the Necromancer thrown down before Day 512.
    // The loop does not turn over — this is the morning that does not repeat.
    if (flags.includes("necromancer_defeated")) {
      setGameState(grantKnowledge(looted, ["campaign_won"]))
      setPendingTraining(null)
      setAntiPlayerContext(null)
      setScreen("victory")
      return
    }

    if (activeBattleId && isAntiPlayerBattle(activeBattleId)) {
      handleAntiPlayerVictory(looted, activeBattleId)
      return
    }

    if (pendingTraining) {
      const { abilityId, level } = pendingTraining
      setGameState(completeAbilityTraining(looted, abilityId, level))
      setPendingTraining(null)
      openDialogue(`training_${abilityId}_${level}`, "location")
      return
    }

    setGameState(looted)
    // A dedicated "<battleId>_victory" dialogue, when one exists, gives real
    // combat a moment of narrative payoff instead of dumping the player
    // straight back to the location screen with nothing to show for it.
    const victoryDialogueId = activeBattleId ? `${activeBattleId}_victory` : null
    if (victoryDialogueId && getDialogueById(victoryDialogueId)) {
      openDialogue(victoryDialogueId, "location")
      return
    }
    setScreen("location")
  }

  // Settings are reachable before a game exists, from the main menu, so this
  // case is handled ahead of the "no game yet" fall-through below.
  if (screen === "settings" && !gameState) {
    return <SettingsScreen onBack={() => setScreen("mainMenu")} />
  }

  // Choosing a face happens before any GameState exists, so it too stands ahead
  // of the fall-through. Cancelling returns to the menu without starting a loop.
  if (screen === "avatarSelect" && !gameState) {
    return (
      <AvatarSelectScreen
        onChoose={handleChooseAvatar}
        onBack={() => setScreen("mainMenu")}
      />
    )
  }

  if (screen === "mainMenu" || !gameState) {
    return (
      <MainMenuScreen
        canContinue={hasSavedGame()}
        onNewGame={handleNewGame}
        onContinue={handleContinue}
        onSettings={() => setScreen("settings")}
      />
    )
  }

  function renderScreen(): ReactNode {
    if (!gameState) return null
    switch (screen) {
      case "location":
        return (
          <LocationScreen
            permanent={gameState.permanent}
            loop={gameState.loop}
            locationId={selectedLocationId ?? gameState.loop.currentLocationId}
            travelDestinationId={travelDestinationId}
            onTravel={handleTravel}
            onCancelJourney={handleCancelJourney}
            onTrainAbility={handleTrainAbility}
            onOpenDialogue={handleOpenDialogue}
            onRest={handleRest}
            onWarnGuard={handleWarnGuard}
            onEvacuate={handleEvacuate}
            onInvestigateRuins={handleInvestigateRuins}
            onRallyGoldenfieldsMilitia={handleRallyGoldenfieldsMilitia}
            onEvacuateGoldenfields={handleEvacuateGoldenfields}
            onSendWordToCrownreach={handleSendWordToCrownreach}
            onWarnIronwatchGarrison={handleWarnIronwatchGarrison}
            onSeekCrownAudience={handleSeekCrownAudience}
            onExposeFalseHistory={handleExposeFalseHistory}
            onPledgeServiceToCrown={handlePledgeServiceToCrown}
            onAcceptCommandOfRealm={handleAcceptCommandOfRealm}
            onCallTheRealmsToMuster={handleCallTheRealmsToMuster}
            onMeetAcademyInstructors={handleMeetAcademyInstructors}
            onSeekElvenCourtAudience={handleSeekElvenCourtAudience}
            onOfferFealtyToElvenCrown={handleOfferFealtyToElvenCrown}
            onSearchTempleRecords={handleSearchTempleRecords}
            onFaceAntiPlayer={handleFaceAntiPlayer}
            onRetakeIronwatch={handleRetakeIronwatch}
            onOfferSeverance={handleOfferSeverance}
            onFactionAction={handleFactionAction}
            onBuyArmor={handleBuyArmor}
            onBack={() => setScreen("worldMap")}
          />
        )
      case "army":
        return (
          <ArmyScreen
            loop={gameState.loop}
            onTakeUnit={handleTakeUnit}
            onTakeAllOfUnit={handleTakeAllOfUnit}
            onTakeAll={handleTakeAll}
            onLeaveUnit={handleLeaveUnit}
            onLeaveAllOfUnit={handleLeaveAllOfUnit}
            onReleaseUnit={handleReleaseUnit}
            onReleaseAllOfUnit={handleReleaseAllOfUnit}
            onBack={() => setScreen("location")}
          />
        )
      case "factions":
        return (
          <FactionsScreen
            permanent={gameState.permanent}
            loop={gameState.loop}
            onBack={() => setScreen("location")}
          />
        )
      case "event":
        return (
          <EventScreen
            key={dialogueContext?.dialogueId}
            dialogueId={dialogueContext?.dialogueId ?? ""}
            permanent={gameState.permanent}
            autoSkipSeen={settings.autoSkipSeenDialogue && !dialogueContext?.replay}
            onContinue={handleEventContinue}
          />
        )
      case "trial":
        return activeTrial ? (
          <TrialScreen
            key={activeTrial.id}
            trial={activeTrial}
            onArrested={handleTrialArrested}
            onCleared={handleTrialCleared}
          />
        ) : null
      case "battle":
        return (
          <BattleScreen
            key={activeBattleId ?? ""}
            battleId={activeBattleId ?? ""}
            playerStacks={
              getBattleById(activeBattleId ?? "")?.nonLethal
                ? []
                : gameState.loop.currentArmy
            }
            permanent={gameState.permanent}
            loopNumber={gameState.loop.loopNumber}
            day={gameState.loop.currentDay}
            battlePaceMs={getBattlePaceDelayMs(settings)}
            onResolve={handleBattleResolve}
          />
        )
      case "death":
        return (
          <DeathScreen
            permanent={gameState.permanent}
            loop={gameState.loop}
            onAwaken={handleAwaken}
          />
        )
      case "victory":
        return (
          <VictoryScreen
            permanent={gameState.permanent}
            loop={gameState.loop}
            onBeginAgain={handleBeginAgainAfterVictory}
            onMainMenu={handleReturnToMainMenu}
          />
        )
      case "journal":
        return (
          <JournalScreen
            permanent={gameState.permanent}
            currentDay={gameState.loop.currentDay}
            onBack={() => setScreen("worldMap")}
          />
        )
      case "skills":
        return (
          <SkillsScreen
            permanent={gameState.permanent}
            onBack={() => setScreen("worldMap")}
          />
        )
      case "history":
        return (
          <HistoryScreen
            currentLoop={gameState.loop.loopNumber}
            onBack={() => setScreen("worldMap")}
          />
        )
      case "settings":
        return <SettingsScreen onBack={() => setScreen("worldMap")} />
      case "spirit":
        return (
          <SpiritScreen
            permanent={gameState.permanent}
            loop={gameState.loop}
            onAskQuestion={handleAskSpiritQuestion}
            onReplayScene={(dialogueId) => openDialogue(dialogueId, "spirit", true)}
            onBack={() => setScreen("worldMap")}
          />
        )
      case "schedule":
        return (
          <ScheduleScreen
            permanent={gameState.permanent}
            loop={gameState.loop}
            onBack={() => setScreen("worldMap")}
          />
        )
      case "worldMap":
      default:
        return (
          <WorldMapScreen
            permanent={gameState.permanent}
            loop={gameState.loop}
            onSelectLocation={handleSelectLocation}
            onAdvanceDay={handleAdvanceDay}
            onSimulateDeath={handleSimulateDeath}
          />
        )
    }
  }

  return (
    <>
      <AppShell
        permanent={gameState.permanent}
        loop={gameState.loop}
        navDisabled={
          screen === "battle" ||
          screen === "trial" ||
          screen === "victory" ||
          !gameState.loop.alive
        }
        activeNav={
          NAV_TARGETS.includes(screen as NavTarget) ? (screen as NavTarget) : null
        }
        celestialRestored={screen === "victory"}
        saveStatus={saveStatus}
        settingsActive={screen === "settings"}
        screenKey={screen}
        contentBackdrop={
          screen === "battle" && activeBattleId
            ? getBattleBackdropValue(activeBattleId)
            : undefined
        }
        onNavigate={handleNavigate}
        onSave={handleSave}
        onOpenSettings={() => setScreen("settings")}
        onMainMenu={handleReturnToMainMenu}
      >
        {renderScreen()}
      </AppShell>
      {digest && (
        <DayDigest
          day={digest.day}
          reports={digest.reports}
          onDismiss={() => setDigest(null)}
        />
      )}
      <Toasts toasts={toasts} onDismiss={dismiss} />
      {loopTransition !== null && (
        <LoopTransition
          loopNumber={loopTransition}
          onDone={() => setLoopTransition(null)}
        />
      )}
      {pendingConfirm && (
        <ConfirmDialog
          title={pendingConfirm.title}
          body={pendingConfirm.body}
          confirmLabel={pendingConfirm.confirmLabel}
          onConfirm={pendingConfirm.onConfirm}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </>
  )
}

export default App
